import {
  Component,
  AfterViewInit,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
} from "@angular/core";

@Component({
  selector: "app-range-slider",
  templateUrl: "./range-slider.component.html",
  styleUrls: ["./range-slider.component.scss"],
})
export class RangeSliderComponent implements AfterViewInit, OnChanges {
  @Input() disabled: boolean = false;

  /**
   * Die vom Parent kommenden Werte.
   * Sobald der Parent `this.currentValues` neu setzt, werden die Slider aktualisiert.
   */
  @Input() rangeValues: { from: number; to: number } = { from: 25, to: 75 };

  /**
   * Gibt Änderungen an den Parent zurück, wenn der Benutzer die Slider bewegt.
   */
  @Output() valueChange = new EventEmitter<{ from: number; to: number }>();

  // Referenzen auf die Slider und deren Tooltips
  private fromSlider!: HTMLInputElement;
  private toSlider!: HTMLInputElement;
  private fromTooltip!: HTMLElement;
  private toTooltip!: HTMLElement;
  private scale!: HTMLElement;

  // Farben für den Slider-Bereich
  private COLOR_TRACK = "rgb(var(--secondary))";
  private COLOR_RANGE = "rgb(var(--primary))";

  /**
   * Nach dem Rendern der View die DOM-Elemente ermitteln und Initialisierung vornehmen.
   */
  ngAfterViewInit(): void {
    this.fromSlider = document.querySelector("#fromSlider") as HTMLInputElement;
    this.toSlider = document.querySelector("#toSlider") as HTMLInputElement;
    this.fromTooltip = document.querySelector("#fromSliderTooltip") as HTMLElement;
    this.toTooltip = document.querySelector("#toSliderTooltip") as HTMLElement;
    this.scale = document.getElementById("scale") as HTMLElement;

    // Initiale Slider-Werte setzen (basierend auf @Input() rangeValues)
    this.setSliderValues(this.rangeValues.from, this.rangeValues.to);

    // Event-Listener, wenn der Benutzer die Slider tatsächlich bewegt
    this.fromSlider.oninput = () => this.handleFromSliderChange();
    this.toSlider.oninput = () => this.handleToSliderChange();

    // Optional: Skala erzeugen, wenn du das in deinem Template willst
    this.createScale(
      parseInt(this.fromSlider.min, 10),
      parseInt(this.fromSlider.max, 10),
      parseInt(this.scale.dataset["steps"] || "10", 10),
      this.scale
    );
  }

  /**
   * Wird aufgerufen, sobald sich @Input() Werte ändern. 
   * -> Wenn der Parent "bidi-charging.component.ts" `this.currentValues = {...}` neu setzt.
   */
  ngOnChanges(changes: SimpleChanges): void {
    // Prüfen, ob rangeValues neu gesetzt wurde und die Slider schon existieren
    if (changes["rangeValues"] && this.fromSlider && this.toSlider) {
      const { from, to } = changes["rangeValues"].currentValue;
      this.setSliderValues(from, to);
    }
  }

  /**
   * Wenn der Nutzer den "fromSlider" bewegt
   */
  private handleFromSliderChange() {
    this.controlFromSlider(
      this.fromSlider,
      this.toSlider,
      this.fromTooltip,
      this.toTooltip,
      this.COLOR_TRACK,
      this.COLOR_RANGE
    );
    this.emitSliderValues(this.fromSlider, this.toSlider);
  }

  /**
   * Wenn der Nutzer den "toSlider" bewegt
   */
  private handleToSliderChange() {
    this.controlToSlider(
      this.fromSlider,
      this.toSlider,
      this.fromTooltip,
      this.toTooltip,
      this.COLOR_TRACK,
      this.COLOR_RANGE
    );
    this.emitSliderValues(this.fromSlider, this.toSlider);
  }

  /**
   * Aktualisiert die Slider auf bestimmte Werte (z.B. wenn Parent sie setzt).
   */
  private setSliderValues(fromValue: number, toValue: number) {
    this.fromSlider.value = fromValue.toString();
    this.toSlider.value = toValue.toString();

    // Fülle den Slider und aktualisiere Tooltips.
    // Du kannst "controlFromSlider" bzw. "controlToSlider" benutzen
    // oder einfach direkt "fillSlider" und "setTooltip" aufrufen, wie du magst.
    this.fillSlider(this.fromSlider, this.toSlider, this.COLOR_TRACK, this.COLOR_RANGE, this.toSlider);
    this.setTooltip(this.fromSlider, this.fromTooltip);
    this.setTooltip(this.toSlider, this.toTooltip);
  }

  /**
   * Emitter an den Parent: Gibt die aktuellen Slider-Werte zurück.
   */
  private emitSliderValues(fromSlider: HTMLInputElement, toSlider: HTMLInputElement) {
    const from = parseInt(fromSlider.value, 10);
    const to = parseInt(toSlider.value, 10);
    this.valueChange.emit({ from, to });
  }

  /**
   * Steuert den "fromSlider" – verhindert, dass er über den "toSlider"-Wert hinausgeht etc.
   */
  private controlFromSlider(
    fromSlider: HTMLInputElement,
    toSlider: HTMLInputElement,
    fromTooltip: HTMLElement,
    toTooltip: HTMLElement,
    trackColor: string,
    rangeColor: string
  ) {
    const [from, to] = this.getParsedValues(fromSlider, toSlider);
    this.fillSlider(fromSlider, toSlider, trackColor, rangeColor, toSlider);
    if (from > to) {
      fromSlider.value = to.toString();
    }
    this.setTooltip(fromSlider, fromTooltip);
  }

  /**
   * Steuert den "toSlider" – verhindert, dass er unter den "fromSlider"-Wert fällt etc.
   */
  private controlToSlider(
    fromSlider: HTMLInputElement,
    toSlider: HTMLInputElement,
    fromTooltip: HTMLElement,
    toTooltip: HTMLElement,
    trackColor: string,
    rangeColor: string
  ) {
    const [from, to] = this.getParsedValues(fromSlider, toSlider);
    this.fillSlider(fromSlider, toSlider, trackColor, rangeColor, toSlider);
    if (from <= to) {
      toSlider.value = to.toString();
    } else {
      toSlider.value = from.toString();
    }
    this.setTooltip(toSlider, toTooltip);
  }

  /**
   * Hilfsfunktion zum Auslesen der Slider-Werte als number-Tuple.
   */
  private getParsedValues(
    fromSlider: HTMLInputElement,
    toSlider: HTMLInputElement
  ): [number, number] {
    const from = parseInt(fromSlider.value, 10);
    const to = parseInt(toSlider.value, 10);
    return [from, to];
  }

  /**
   * Füllt den Slider-Hintergrund zwischen "from" und "to" – 
   * für die visuelle Darstellung der Range.
   */
  private fillSlider(
    from: HTMLInputElement,
    to: HTMLInputElement,
    trackColor: string,
    rangeColor: string,
    controlSlider: HTMLInputElement
  ) {
    const rangeDistance = parseInt(to.max, 10) - parseInt(to.min, 10);
    const fromPosition = from.valueAsNumber - parseInt(to.min, 10);
    const toPosition = to.valueAsNumber - parseInt(to.min, 10);

    controlSlider.style.background = `linear-gradient(
      to right,
      ${trackColor} 0%,
      ${trackColor} ${(fromPosition / rangeDistance) * 100}%,
      ${rangeColor} ${(fromPosition / rangeDistance) * 100}%,
      ${rangeColor} ${(toPosition / rangeDistance) * 100}%, 
      ${trackColor} ${(toPosition / rangeDistance) * 100}%, 
      ${trackColor} 100%)`;
  }

  /**
   * Platziert und aktualisiert das Tooltip für einen bestimmten Slider.
   */
  private setTooltip(slider: HTMLInputElement, tooltip: HTMLElement) {
    const value = slider.valueAsNumber;
    tooltip.textContent = `${value}%`;

    const percent =
      ((value - parseInt(slider.min, 10)) /
        (parseInt(slider.max, 10) - parseInt(slider.min, 10))) *
      100;
    tooltip.style.left = `calc(${percent}% - 0px)`;
  }

  /**
   * Erzeugt eine Skala (Ticks) unterhalb des Sliders.
   * (Optionale Funktion, falls du die Skala tatsächlich einsetzt.)
   */
  private createScale(
    min: number,
    max: number,
    step: number,
    scaleElement: HTMLElement
  ) {
    const range = max - min;
    const steps = range / step;
    for (let i = 0; i <= steps; i++) {
      const value = min + i * step;
      const percent = ((value - min) / range) * 100;

      const marker = document.createElement("div");
      marker.style.left = `${percent}%`;
      marker.textContent = `${value}%`;
      scaleElement.appendChild(marker);
    }
  }
}
